import { useState, useRef } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import api, { EXCEL_IMPORT_ROLES, UserRole } from '@/lib/api'
import { Upload, FileSpreadsheet, Check, AlertCircle, ShieldAlert, Download } from 'lucide-react'

type ImportMode = 'contacts' | 'projects'

const CONTACT_FIELDS = ['client_name', 'email', 'phone', 'company', 'source', 'priority', 'status', 'notes']
const PROJECT_FIELDS = ['project_code', 'project_type', 'client_name', 'phone', 'address', 'date_completed', 'service_expiry_date', 'team_leader', 'sales_person', 'description', 'status', 'map_link']

export default function ExcelImport() {
  const currentUser = JSON.parse(localStorage.getItem('user') || '{}')
  const userRole = currentUser.role as UserRole
  const canImport = (EXCEL_IMPORT_ROLES as readonly string[]).includes(userRole)

  if (!canImport) {
    return (
      <div className="space-y-6">
        <h1 className="text-2xl font-bold text-slate-900">Excel Importer</h1>
        <div className="bg-white rounded-xl border border-slate-200 p-8 text-center">
          <ShieldAlert className="mx-auto text-slate-400 mb-4" size={48} />
          <h3 className="font-medium text-slate-900 mb-2">Access Restricted</h3>
          <p className="text-sm text-slate-500">You do not have permission to import data. This feature is available to sales and admin roles only.</p>
        </div>
      </div>
    )
  }

  const queryClient = useQueryClient()
  const fileRef = useRef<HTMLInputElement>(null)
  const [mode, setMode] = useState<ImportMode>('projects')
  const [file, setFile] = useState<File | null>(null)
  const [preview, setPreview] = useState<any[]>([])
  const [headers, setHeaders] = useState<string[]>([])
  const [mapping, setMapping] = useState<Record<string, string>>({})
  const [result, setResult] = useState<{ imported: number; skipped: number; errors?: number } | null>(null)
  const [error, setError] = useState('')

  const fields = mode === 'contacts' ? CONTACT_FIELDS : PROJECT_FIELDS
  const requiredField = mode === 'contacts' ? 'client_name' : 'project_code'

  const resetState = () => {
    setFile(null)
    setPreview([])
    setHeaders([])
    setMapping({})
    setResult(null)
    setError('')
    if (fileRef.current) fileRef.current.value = ''
  }

  const switchMode = (m: ImportMode) => {
    setMode(m)
    resetState()
  }

  const autoMapHeaders = (hdrs: string[], targetFields: string[]) => {
    const autoMap: Record<string, string> = {}
    hdrs.forEach(h => {
      const lower = h.toLowerCase().replace(/[_\s-]/g, '')
      if (mode === 'contacts') {
        if (lower.includes('name') && !lower.includes('company')) autoMap[h] = 'client_name'
        else if (lower.includes('email')) autoMap[h] = 'email'
        else if (lower.includes('phone')) autoMap[h] = 'phone'
        else if (lower.includes('company') || lower.includes('org')) autoMap[h] = 'company'
        else if (lower.includes('source')) autoMap[h] = 'source'
        else if (lower.includes('priority')) autoMap[h] = 'priority'
      } else {
        const match = targetFields.find(f => f.replace(/_/g, '') === lower)
        if (match) autoMap[h] = match
        else if (lower.includes('projectcode') || lower === 'code') autoMap[h] = 'project_code'
        else if (lower.includes('projecttype') || lower === 'type') autoMap[h] = 'project_type'
        else if (lower.includes('clientname') || (lower.includes('name') && !lower.includes('team') && !lower.includes('sales'))) autoMap[h] = 'client_name'
        else if (lower.includes('teamleader')) autoMap[h] = 'team_leader'
        else if (lower.includes('salesperson')) autoMap[h] = 'sales_person'
        else if (lower.includes('datecompleted') || lower === 'completed') autoMap[h] = 'date_completed'
        else if (lower.includes('expirydate') || lower.includes('serviceexpiry')) autoMap[h] = 'service_expiry_date'
        else if (lower.includes('maplink') || lower.includes('gps') || lower.includes('googlemaps')) autoMap[h] = 'map_link'
      }
    })
    return autoMap
  }

  const handleFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0]
    if (!f) return
    setFile(f)
    setResult(null)
    setError('')

    try {
      const XLSX = await import('https://cdn.sheetjs.com/xlsx-0.20.1/package/xlsx.mjs' as any).catch(() => null)
      if (!XLSX) {
        setError('Could not load Excel parser. Please ensure xlsx is available.')
        return
      }
      const data = await f.arrayBuffer()
      const wb = XLSX.read(data)
      const ws = wb.Sheets[wb.SheetNames[0]]
      const json = XLSX.utils.sheet_to_json(ws, { header: 1 }) as any[][]
      if (json.length < 2) { setError('File is empty or has no data rows'); return }
      const hdrs = json[0].map(String)
      setHeaders(hdrs)
      const rows = json.slice(1, 11).map(row => {
        const obj: any = {}
        hdrs.forEach((h, i) => { obj[h] = row[i] ?? '' })
        return obj
      })
      setPreview(rows)
      setMapping(autoMapHeaders(hdrs, fields))
    } catch {
      setError('Failed to parse file')
    }
  }

  const importMutation = useMutation({
    mutationFn: async () => {
      if (!file) throw new Error('No file')
      const XLSX = await import('https://cdn.sheetjs.com/xlsx-0.20.1/package/xlsx.mjs' as any)
      const data = await file.arrayBuffer()
      const wb = XLSX.read(data)
      const ws = wb.Sheets[wb.SheetNames[0]]
      const json = XLSX.utils.sheet_to_json(ws, { header: 1 }) as any[][]
      const hdrs = json[0].map(String)
      const records = json.slice(1).map(row => {
        const rec: any = {}
        Object.entries(mapping).forEach(([header, field]) => {
          const idx = hdrs.indexOf(header)
          if (idx >= 0 && field) rec[field] = String(row[idx] ?? '')
        })
        return rec
      }).filter(r => r[requiredField])
      const endpoint = mode === 'contacts' ? '/clients/import' : '/projects/import'
      return api.post(endpoint, { records }).then(r => r.data)
    },
    onSuccess: (data) => {
      setResult(data)
      queryClient.invalidateQueries({ queryKey: mode === 'contacts' ? ['clients'] : ['projects'] })
    },
    onError: (err: any) => setError(err.message || 'Import failed'),
  })

  return (
    <div className="space-y-6">
      <h1 className="text-2xl font-bold text-slate-900">Excel Importer</h1>

      <div className="flex border-b border-slate-200">
        <button onClick={() => switchMode('contacts')}
          className={`px-5 py-2.5 text-sm font-medium border-b-2 transition-colors ${mode === 'contacts' ? 'border-red-800 text-red-800' : 'border-transparent text-slate-500 hover:text-slate-700'}`}>
          Contacts Import
        </button>
        <button onClick={() => switchMode('projects')}
          className={`px-5 py-2.5 text-sm font-medium border-b-2 transition-colors ${mode === 'projects' ? 'border-red-800 text-red-800' : 'border-transparent text-slate-500 hover:text-slate-700'}`}>
          Project Bank Import
        </button>
      </div>

      <div className="bg-white rounded-xl border border-slate-200 p-8 text-center">
        <div className="max-w-md mx-auto">
          <FileSpreadsheet className="mx-auto text-slate-400 mb-4" size={48} />
          <h3 className="font-medium text-slate-900 mb-2">
            Upload {mode === 'contacts' ? 'Client' : 'Project'} Data
          </h3>
          <p className="text-sm text-slate-500 mb-4">
            {mode === 'contacts'
              ? 'Upload .xlsx or .csv file with client data. Duplicates (by email) will be skipped.'
              : 'Upload .xlsx or .csv file with project data. Duplicates (by project code + type) will be skipped.'}
          </p>
          <div className="flex items-center justify-center gap-3">
            <label className="px-6 py-2.5 bg-red-800 text-white rounded-lg text-sm font-medium hover:bg-red-900 inline-flex items-center gap-2 cursor-pointer">
              <Upload size={16} /> Choose File
              <input ref={fileRef} type="file" accept=".xlsx,.xls,.csv" onChange={handleFile} className="hidden" />
            </label>
            {mode === 'projects' && (
              <a href="/project_bank_import_sample.csv" download
                className="px-4 py-2.5 border border-slate-300 text-slate-700 rounded-lg text-sm font-medium hover:bg-slate-50 inline-flex items-center gap-2">
                <Download size={16} /> Sample CSV
              </a>
            )}
          </div>
          {file && <p className="mt-2 text-sm text-slate-600">{file.name}</p>}
        </div>
      </div>

      {error && (
        <div className="bg-red-50 text-red-600 px-4 py-3 rounded-lg text-sm flex items-center gap-2">
          <AlertCircle size={16} /> {error}
        </div>
      )}

      {result && (
        <div className="bg-green-50 text-green-700 px-4 py-3 rounded-lg text-sm flex items-center gap-2">
          <Check size={16} /> Imported {result.imported} records, skipped {result.skipped} duplicates
          {result.errors ? `, ${result.errors} errors` : ''}
        </div>
      )}

      {preview.length > 0 && (
        <>
          <div className="bg-white rounded-xl border border-slate-200 p-5">
            <h3 className="font-medium text-slate-900 mb-3">Column Mapping</h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {headers.map(h => (
                <div key={h}>
                  <label className="text-xs text-slate-500 block mb-1">{h}</label>
                  <select value={mapping[h] || ''} onChange={e => setMapping({ ...mapping, [h]: e.target.value })}
                    className="w-full px-2 py-1.5 text-sm border rounded-lg">
                    <option value="">Skip</option>
                    {fields.map(f => <option key={f} value={f}>{f}</option>)}
                  </select>
                </div>
              ))}
            </div>
          </div>

          <div className="bg-white rounded-xl border border-slate-200 p-5">
            <h3 className="font-medium text-slate-900 mb-3">Preview (first 10 rows)</h3>
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead><tr className="border-b">
                  {headers.map(h => <th key={h} className="text-left py-2 px-2 font-medium text-slate-600">{h}</th>)}
                </tr></thead>
                <tbody>
                  {preview.map((row, i) => (
                    <tr key={i} className="border-b border-slate-100">
                      {headers.map(h => <td key={h} className="py-1.5 px-2">{String(row[h] ?? '')}</td>)}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          <button onClick={() => importMutation.mutate()} disabled={importMutation.isPending || !Object.values(mapping).some(v => v === requiredField)}
            className="px-6 py-2.5 bg-red-800 text-white rounded-lg text-sm font-medium hover:bg-red-900 disabled:opacity-50 inline-flex items-center gap-2">
            <Upload size={16} /> {importMutation.isPending ? 'Importing...' : `Import ${mode === 'contacts' ? 'Contacts' : 'Projects'}`}
          </button>
        </>
      )}
    </div>
  )
}
